import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Header } from '../types';
import { HEADER_COLUMNS } from '../constants/columns';
import { formatDate } from '../utils/dateFormatter';
import ExcelFilter from './ExcelFilter';
import { useReopenRepairOrderService } from '../services/reopenRepairOrderService';
import { useAuthorizeProblemSolver } from '../hooks/useAuthorizeProblemSolver';

interface RadSite {
  organizationId: string;
  organizationName: string;
}

interface HeaderTableProps {
  headers: Header[];
  onHeaderClick: (header: Header) => void;
  selectedHeader?: Header;
  isDarkTheme?: boolean;
  linesLoading?: boolean;
  onHeaderUpdate?: (headerId: string, field: string, value: string, organizationId?: string) => void;
  radSites?: RadSite[];
  radSitesLoading?: boolean;
  onLoadRadSites?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loading?: boolean;
  userId?: string;
  onReopenSuccess?: (requestNumber: string) => void; // Add callback for reopen success
}

const HeaderTable: React.FC<HeaderTableProps> = ({ 
  headers, 
  onHeaderClick, 
  selectedHeader, 
  isDarkTheme, 
  linesLoading = false,
  onHeaderUpdate,
  radSites = [],
  radSitesLoading = false,
  onLoadRadSites,
  onLoadMore,
  hasMore = false,
  loading = false,
  userId,
  onReopenSuccess
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [unlockedOrders, setUnlockedOrders] = useState<Set<string>>(new Set());
  const [showReopenColumn, setShowReopenColumn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const { reopenRepairOrder, loading: reopenLoading } = useReopenRepairOrderService();
  const { authorizeProblemSolver } = useAuthorizeProblemSolver();
  const [filters, setFilters] = useState<Record<string, string[]>>({
    'Request Number': [],
    'From Site': [],
    'To Site': []
  });

  // Check authorization on component mount
  useEffect(() => {
    const checkAuthorization = async () => {
      if (userId && !authChecked) {
        setAuthChecked(true);
        const result = await authorizeProblemSolver({ userid: userId });
        setShowReopenColumn(result?.data?.[0]?.authorizeProbleSolver === "Y");
      }
    };
    checkAuthorization();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Filter columns based on authorization
  const visibleColumns = useMemo(() => {
    return showReopenColumn ? HEADER_COLUMNS : HEADER_COLUMNS.filter(col => col !== 'Reopen Request');
  }, [showReopenColumn]);

  const filteredHeaders = useMemo(() => {
    return headers.filter(header => {
      const requestNumberMatch = filters['Request Number'].length === 0 || 
        filters['Request Number'].includes(header.requestNumber);
      const fromSiteMatch = filters['From Site'].length === 0 || 
        filters['From Site'].includes(header.fromSite);
      const toSiteMatch = filters['To Site'].length === 0 || 
        filters['To Site'].includes(header.toSite);
      
      return requestNumberMatch && fromSiteMatch && toSiteMatch;
    });
  }, [headers, filters]);

  const handleFilterChange = (column: string, selectedValues: string[]) => {
    setFilters(prev => ({
      ...prev,
      [column]: selectedValues
    }));
  };

  // Handle scroll for infinite loading
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current || !hasMore || loading || !onLoadMore) return;
      
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 5) {
        onLoadMore();
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [hasMore, loading, onLoadMore]);

  const getColumnValues = (column: string): string[] => {
    const fieldMap: Record<string, keyof Header> = {
      'Request Number': 'requestNumber',
      'From Site': 'fromSite',
      'To Site': 'toSite',
      'Ship From Address': 'fromSiteAddress'
    };
    return headers.map(header => String(header[fieldMap[column]] || ''));
  };
  const getHeaderValue = (header: Header, column: string): string => {
    const fieldMap: Record<string, keyof Header> = {
      'Request Number': 'requestNumber',
      'From Site': 'fromSite',
      'Ship From Address': 'fromSiteAddress',
      'To Site': 'toSite',
      'Type': 'type',
      'Status': 'status',
      'Creation Date': 'creationDate',
      'Tracking Number': 'trackingNumber',
      'Tracking Status': 'trackingStatus',
      'Expected Delivery Date': 'expectedDeliveryDate',
      'Delivered Date': 'deliveredDate',
      'BU': 'region',
      'Received At RAD': 'receivedAtRad'
    };
    
    // Handle Reopen Request column
    if (column === 'Reopen Request') {
      return ''; // Return empty string, will be handled separately in render
    }
    
    const value = header[fieldMap[column]] || '';
    const dateColumns = ['Creation Date', 'Expected Delivery Date', 'Delivered Date'];
    
    if (dateColumns.includes(column) && value) {
      return formatDate(String(value));
    }

    if ((column === 'From Site' || column === 'To Site') && value) {
      const str = String(value);
      return str.includes('-') ? str.substring(0, str.indexOf('-')) : str;
    }
    
    return String(value);
  };

  return (
    <div style={{ marginBottom: '10px' }} data-tour="header-table">
      <style>
        {`
          .thick-scrollbar::-webkit-scrollbar {
            width: 7px;
          }
          .thick-scrollbar::-webkit-scrollbar-track {
            background: ${isDarkTheme ? '#2a2a2a' : '#f1f1f1'};
          }
          .thick-scrollbar::-webkit-scrollbar-thumb {
            background: ${isDarkTheme ? '#555' : '#888'};
            border-radius: 8px;
          }
          .thick-scrollbar::-webkit-scrollbar-thumb:hover {
            background: ${isDarkTheme ? '#777' : '#555'};
          }
        `}
      </style>
      <div 
        ref={scrollContainerRef}
        style={{ 
          maxHeight: '318px', 
          overflowY: 'auto', 
          border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`
        }}
        className="thick-scrollbar"
      >
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0' }}>
        <thead>
          <tr style={{ backgroundColor: isDarkTheme ? '#444' : '#f5f5f5' }}>
            {visibleColumns.map((column) => {
              const hasFilter = ['Request Number', 'From Site', 'To Site'].includes(column);
              return (
                <th key={column} style={{ 
                  padding: '8px', 
                  border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, 
                  textAlign: 'left', 
                  color: isDarkTheme ? '#fff' : '#000', 
                  fontSize: '13px', 
                  position: 'sticky', 
                  top: '0', 
                  backgroundColor: isDarkTheme ? '#444' : '#f5f5f5', 
                  zIndex: 100,
                  height: '40px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  width: column === 'Request Number' ? '90px' : column === 'From Site' ? '70px' : column === 'To Site' ? '70px' : column === 'Received At RAD' ? '60px' : 'auto',
                  minWidth: column === 'Request Number' ? '90px' : column === 'From Site' ? '70px' : column === 'To Site' ? '70px' : column === 'Received At RAD' ? '60px' : 'auto',
                  whiteSpace: ['Creation Date', 'Expected Delivery Date', 'Delivered Date'].includes(column) ? 'nowrap' : 'normal'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{column}</span>
                    {hasFilter && (
                      <ExcelFilter
                        column={column}
                        values={getColumnValues(column)}
                        selectedValues={filters[column]}
                        onFilterChange={handleFilterChange}
                        isDarkTheme={isDarkTheme}
                      />
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {filteredHeaders.length === 0 ? (
            <tr style={{ backgroundColor: isDarkTheme ? '#2a2a2a' : 'white', color: isDarkTheme ? '#fff' : '#000' }}>
              {visibleColumns.map((column) => (
                <td key={column} style={{ padding: '8px', border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, color: isDarkTheme ? '#fff' : '#000', fontSize: '12px' }}>
                  
                </td>
              ))}
            </tr>
          ) : (
            filteredHeaders.map((header, index) => (
              <tr
                key={index}
                onClick={() => !linesLoading && onHeaderClick(header)}
                style={{
                  cursor: linesLoading ? 'not-allowed' : 'pointer',
                  backgroundColor: selectedHeader?.requestNumber === header.requestNumber 
                    ? (isDarkTheme ? '#0d47a1' : '#e3f2fd') 
                    : (isDarkTheme ? '#2a2a2a' : 'white'),
                  color: isDarkTheme ? '#fff' : '#000',
                  opacity: linesLoading && selectedHeader?.requestNumber !== header.requestNumber ? 0.6 : 1
                }}
              >
                {visibleColumns.map((column) => {
                  const cellValue = getHeaderValue(header, column);
                  
                  return (
                    <td key={column} style={{ 
                      padding: '4px', 
                      border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`, 
                      color: isDarkTheme ? '#fff' : '#000', 
                      fontSize: '12px',
                      width: column === 'Request Number' ? '90px' : column === 'From Site' ? '70px' : column === 'To Site' ? '70px' : column === 'Reopen Request' ? '50px' : column === 'Received At RAD' ? '60px' : 'auto',
                      minWidth: column === 'Request Number' ? '90px' : column === 'From Site' ? '70px' : column === 'To Site' ? '70px' : column === 'Reopen Request' ? '50px' : column === 'Received At RAD' ? '60px' : 'auto',
                      textAlign: column === 'Reopen Request' ? 'center' : 'left',
                      whiteSpace: ['Creation Date', 'Expected Delivery Date', 'Delivered Date', 'Received At RAD'].includes(column) ? 'nowrap' : 'normal'
                    }} >
                      {column === 'Reopen Request' ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          {unlockedOrders.has(header.requestNumber) ? (
                            <svg 
                              width="16" 
                              height="18" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke={isDarkTheme ? '#fff' : '#000'} 
                              strokeWidth="2"
                              style={{ cursor: header.status === 'Approved' ? 'pointer' : 'not-allowed', opacity: header.status === 'Approved' ? 1 : 0.5 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (header.status === 'Approved') {
                                  setUnlockedOrders(prev => {
                                    const newSet = new Set(prev);
                                    newSet.delete(header.requestNumber);
                                    return newSet;
                                  });
                                }
                              }}
                            >
                              <rect x="3" y="11" width="18" height="10" rx="2" ry="2"></rect>
                              <path d="M7 11V7a5 5 0 0 1 8-4m0 0v4"></path>
                            </svg>
                          ) : (
                            <svg 
                              width="16" 
                              height="18" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke={isDarkTheme ? '#fff' : '#000'} 
                              strokeWidth="2"
                              style={{ cursor: header.status === 'Approved' ? 'pointer' : 'not-allowed', opacity: header.status === 'Approved' ? 1 : 0.5 }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (header.status === 'Approved' && !reopenLoading) {
                                  try {
                                    await reopenRepairOrder({ selectedHeader: header });
                                    setUnlockedOrders(prev => new Set(prev).add(header.requestNumber));
                                    // Call the callback to refresh the header record
                                    if (onReopenSuccess) {
                                      onReopenSuccess(header.requestNumber);
                                    }
                                  } catch (error: any) {
                                    alert(`Reopen failed: ${error.message}`);
                                  }
                                }
                              }}
                            >
                              <rect x="3" y="11" width="18" height="10" rx="2" ry="2"></rect>
                              <circle cx="12" cy="7" r="4"></circle>
                              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                            </svg>
                          )}
                        </div>
                      ) : (
                        <div style={{ minHeight: '16px', padding: '0px' }}>{cellValue}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))
          )}

        </tbody>
        </table>
        {loading && (
          <>
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
            <div style={{ textAlign: 'center', padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid #f3f3f3",
                  borderTop: "2px solid #007bff",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite"
                }}
              />
              <span style={{ color: isDarkTheme ? '#fff' : '#000' }}>Loading more records...</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HeaderTable;